// src/components/tiptap/nodes.ts
// Block-level nodes for the radiology report editor.
//
// The doc is laid out as:
//
//   doc
//   ├── findingsSection
//   │   └── organSubsection{ kind, label }+
//   │       └── paragraph (with finding marks inline)
//   └── impressionSection
//       └── impressionItem{ id, citedFindings, provenance }+
//           └── paragraph (with finding marks inline)
//
// OrganSubsection renders its label ("LIVER:") via a NodeView button outside
// contentDOM — the rad cannot accidentally backspace into it because it
// isn't part of the editor model. Clicking the label opens rename + remove
// actions handled by the shared useSectionContextMenu
// hook (mounted by both the case viewer and the template editor). All other
// section/item labels still render via CSS pseudo-elements keyed off data-*
// attrs (ImpressionItem numbering).
//
// All four nodes are `defining` + `isolating` so block-level edits
// don't merge across section boundaries. Adjacent organ subsections
// stay distinct even when both are empty.

import { Node, mergeAttributes } from "@tiptap/core";

// ---------------------------------------------------------------------------
// FindingsSection — the FINDINGS half of the report. Contains exactly the
// 16 organ subsections defined by the active template, in canonical order.
// ---------------------------------------------------------------------------
export const FindingsSectionNode = Node.create({
  name: "findingsSection",
  group: "block",
  content: "organSubsection+",
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: "section[data-section-kind='findings']" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "section",
      mergeAttributes(HTMLAttributes, {
        "data-section-kind": "findings",
        class: "report-section findings-section",
      }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// ImpressionSection — the IMPRESSION half. Contains numbered impression
// items, each citing the findings it summarizes. Auto-generated by the
// streaming impression endpoint; the rad reviews and edits.
// ---------------------------------------------------------------------------
export const ImpressionSectionNode = Node.create({
  name: "impressionSection",
  group: "block",
  // Allow empty — the impression section exists from the moment the report
  // loads but stays empty until the LLM streams items in.
  content: "impressionItem*",
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      findingsChanged: {
        // Missing on older reports: no claim about their historical generation state.
        default: null,
        parseHTML: (el) => {
          const value = el.getAttribute("data-findings-changed");
          return value === null ? null : value === "true";
        },
        renderHTML: (attrs) =>
          typeof attrs.findingsChanged === "boolean"
            ? { "data-findings-changed": String(attrs.findingsChanged) }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "section[data-section-kind='impression']" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "section",
      mergeAttributes(HTMLAttributes, {
        "data-section-kind": "impression",
        class: "report-section impression-section",
      }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// OrganSubsection — one organ-system block inside FindingsSection (LIVER,
// SPLEEN, etc.). The label is structural (rendered via CSS), not editable
// text. `kind` is the slug from the template (e.g. "liver"); `label` is
// the display string ("LIVER").
// ---------------------------------------------------------------------------
export interface OrganSubsectionAttrs {
  kind: string;
  label: string;
}

export const OrganSubsectionNode = Node.create({
  name: "organSubsection",
  group: "block",
  // Exactly one paragraph. Multi-paragraph content is structurally
  // wrong — each organ subsection is one clinical line. Paste of
  // multi-line plain text gets coerced by PM into the single paragraph.
  content: "paragraph",
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      kind: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-kind") ?? "",
        renderHTML: (attrs) => (attrs.kind ? { "data-kind": attrs.kind } : {}),
      },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") ?? "",
        renderHTML: (attrs) => (attrs.label ? { "data-label": attrs.label } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-organ-subsection]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-organ-subsection": "true",
        class: "organ-subsection",
      }),
      0,
    ];
  },

  // NodeView renders the label "LIVER: " as a button outside contentDOM
  // (so PM never parses it as paragraph content and editor.getHTML()
  // round-trips through renderHTML, keeping the stored shape
  // `<div ...><p>...</p></div>`). Label styling lives in ReporterStyles.css
  // (.organ-subsection__label).
  //
  // Native button activation supplies mouse, touch and keyboard access to the
  // same Rename/Remove menu in both editors. Right-click remains a shortcut.
  addNodeView() {
    return ({ node, editor }) => {
      const dom = document.createElement("div");
      dom.setAttribute("data-organ-subsection", "true");
      if (node.attrs.kind) dom.setAttribute("data-kind", node.attrs.kind);
      if (node.attrs.label) dom.setAttribute("data-label", node.attrs.label);
      dom.className = "organ-subsection";

      const label = document.createElement("button");
      label.type = "button";
      label.disabled = !editor.isEditable;
      label.className = "organ-subsection__label";
      label.setAttribute("contenteditable", "false");
      label.setAttribute("aria-label", `${node.attrs.label} section`);
      label.setAttribute("aria-haspopup", "menu");
      label.setAttribute("aria-expanded", "false");
      label.title = "Section actions";
      label.textContent = `${node.attrs.label}: `;

      // contentDOM must NOT be a <p>: PM mounts the child paragraph node as
      // its own <p>, and nesting two <p>s is invalid HTML (the parser would
      // auto-close the outer when a serialized round-trip happens). Use a
      // generic <div> wrapper; CSS keeps it inline so the chip row flows
      // right after the label.
      const contentDOM = document.createElement("div");
      contentDOM.className = "organ-subsection__content";
      dom.append(label, contentDOM);

      return {
        dom,
        contentDOM,
        // Let the native button own focus and Enter/Space; those keys must not
        // insert content or navigate fields through the editor's keymaps.
        stopEvent: (event) => label.contains(event.target as globalThis.Node),
        update(updated) {
          if (updated.type.name !== "organSubsection") return false;
          // Always re-apply attrs to the DOM. We can't compare against
          // `node.attrs.label` to skip no-ops — the closure's `node` is
          // pinned to the original Node from createView time, while the
          // ACTUAL DOM state reflects whatever update() last set. An undo
          // step that reverts attrs back to the closure-original would
          // otherwise be a silent no-op (DOM keeps the post-edit value).
          const nextLabel = (updated.attrs.label as string) ?? "";
          const nextKind = (updated.attrs.kind as string) ?? "";
          label.textContent = `${nextLabel}: `;
          label.setAttribute("aria-label", `${nextLabel} section`);
          dom.setAttribute("data-label", nextLabel);
          dom.setAttribute("data-kind", nextKind);
          return true;
        },
        // Mutations inside the label are ours; update() rewrites
        // label.textContent + aria when attrs change. PM should only react to
        // mutations inside contentDOM.
        ignoreMutation(mutation) {
          if (mutation.type === "selection") return false;
          return !contentDOM.contains(mutation.target);
        },
      };
    };
  },
});

// ---------------------------------------------------------------------------
// ImpressionItem — one numbered conclusion in IMPRESSION. Stable `id` lets
// the LLM (and the rad) reference items across re-generation. `citedFindings`
// links each item back to the finding ids it summarizes; clicking the item
// can highlight/jump to those findings. `provenance` distinguishes
// template-authored, AI-generated, user-typed, and rad-edited items so
// impression regen preserves manual edits and presentation never has to infer
// the source from wording or ids.
// ---------------------------------------------------------------------------
export type ImpressionItemProvenance = "template" | "ai-detected" | "user" | "modified";

export interface ImpressionItemAttrs {
  id: string;
  citedFindings: string[];
  provenance: ImpressionItemProvenance;
}

export const ImpressionItemNode = Node.create({
  name: "impressionItem",
  group: "block",
  // Exactly one paragraph — each impression item is one clinical
  // conclusion. Same coercion semantics as OrganSubsection.
  content: "paragraph",
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      id: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-id") ?? "",
        renderHTML: (attrs) => (attrs.id ? { "data-id": attrs.id } : {}),
      },
      citedFindings: {
        default: [] as string[],
        parseHTML: (el) => {
          const raw = el.getAttribute("data-cited-findings");
          if (!raw) return [];
          try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed)
              ? parsed.filter((x): x is string => typeof x === "string")
              : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attrs) => {
          const cited = attrs.citedFindings as string[] | undefined;
          return cited && cited.length > 0 ? { "data-cited-findings": JSON.stringify(cited) } : {};
        },
      },
      provenance: {
        default: "ai-detected" as ImpressionItemProvenance,
        parseHTML: (el) =>
          (el.getAttribute("data-provenance") as ImpressionItemProvenance) || "ai-detected",
        renderHTML: (attrs) => (attrs.provenance ? { "data-provenance": attrs.provenance } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-impression-item]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-impression-item": "true",
        class: "impression-item",
      }),
      0,
    ];
  },
});
